import type { FC } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import cx from 'classnames';
import { LoadingIndicator } from '@THC/components/LoadingIndicator';
import { MDText } from 'i18n-react';
import { useStore } from 'zustand';

import {
	triggerAiCompanionExistingChatClickedEvent,
	triggerAiCompanionNewChatClickedEvent,
} from 'Services/analytics/events/aiCompanionEvents';
import VersionBadge from 'Storybook/local/VersionBadge/VersionBadge';
import useFeatureToggle, { FEATURES } from 'Utilities/hooks/useFeatureToggle';

import { ChatItems } from './components/ChatItems';
import { ConversationSidebar } from './components/ConversationSidebar';
import { ExampleChats } from './components/ExampleChats';
import { MessageField } from './components/MessageField';
import LocalTexts from './AiCompanion.texts';
import { conversationService, ConversationStore } from './services';
import { useScrollToLatestMessage } from './useScrollToLatestMessage';

import styles from './AiCompanion.module.scss';

const LocalT = new MDText(LocalTexts);

export const AiCompanion: FC<{ hideMobileButton?: boolean }> = ({ hideMobileButton }) => {
	const chatRef = useRef<HTMLDivElement>(null);
	const mostRecentUserItemRef = useRef<HTMLDivElement | null>(null);

	const { items, loading, thinking } = useStore(ConversationStore);
	const isDealerAgentServiceEnabled = useFeatureToggle(FEATURES.enableDealerAgentService);
	const isAiCompanionMultiConversationEnabled = useFeatureToggle(FEATURES.enableAiCompanionMultiConversation);

	const isExampleChatsEnabled = isDealerAgentServiceEnabled && isAiCompanionMultiConversationEnabled;
	const showEmptyState = !(loading && isAiCompanionMultiConversationEnabled) && !items.length;

	const { isScrollReady } = useScrollToLatestMessage({
		chatContainerRef: chatRef,
		items,
		mostRecentUserItemRef,
		thinking,
	});

	useEffect(() => {
		void conversationService.initConversation({
			isDealerAgentServiceEnabled,
			isMultiConversationEnabled: isAiCompanionMultiConversationEnabled,
		});

		return () => {
			conversationService.cancelPendingPoll();
			conversationService.cancelActiveStreams();
			conversationService.cancelHistoryFetch();
		};
	}, [isDealerAgentServiceEnabled, isAiCompanionMultiConversationEnabled]);

	const handleSelectConversation = useCallback((conversationId: string) => {
		triggerAiCompanionExistingChatClickedEvent({ conversationId });
		void conversationService.selectConversation(conversationId);
	}, []);

	const handleNewConversation = useCallback(() => {
		triggerAiCompanionNewChatClickedEvent();
		conversationService.startNewConversation();
		window.scrollTo({ top: 0 });
	}, []);

	return (
		<div className={cx(styles.outerWrapper, isAiCompanionMultiConversationEnabled && styles.multiConversationLayout)}>
			{isAiCompanionMultiConversationEnabled && (
				<ConversationSidebar
					hideMobileButton={hideMobileButton}
					onNewConversation={handleNewConversation}
					onSelectConversation={handleSelectConversation}
				/>
			)}
			<div
				className={cx(
					styles.wrapper,
					isAiCompanionMultiConversationEnabled && styles.multiConversation,
					!!items.length && styles.fixed,
				)}
			>
				{loading && isAiCompanionMultiConversationEnabled && (
					<div className={styles.loadingSpinner}>
						<LoadingIndicator data-spinner-busy variant="spinner" />
					</div>
				)}
				{showEmptyState && (
					<div className={styles.header}>
						<VersionBadge text="Beta" />
						<h2 className={styles.title}>{LocalT.translate('title')}</h2>
						<h5 className={styles.subtitle}>{LocalT.translate('subtitle')}</h5>
					</div>
				)}
				<div
					ref={chatRef}
					className={cx(
						styles.chatContainer,
						(!isScrollReady || (loading && isAiCompanionMultiConversationEnabled)) && styles.chatContainerHidden,
					)}
				>
					<ChatItems ref={mostRecentUserItemRef} items={items} thinking={thinking} />
				</div>
				{!(loading && isAiCompanionMultiConversationEnabled) && (
					<MessageField
						hasItems={!!items.length}
						isBusy={loading}
						isMultiConversation={isAiCompanionMultiConversationEnabled}
						onSendMessage={
							isAiCompanionMultiConversationEnabled
								? conversationService.sendUserRequestStream
								: conversationService.sendUserRequest
						}
						thinking={thinking}
					/>
				)}
				{showEmptyState && isExampleChatsEnabled && (
					<ExampleChats onSelectChat={conversationService.sendUserRequestStream} />
				)}
			</div>
		</div>
	);
};
